import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Plus, Trash2, Upload, Clock, Layers, GraduationCap, BookOpen, CheckCircle, Circle, Calendar, Save, Search, X, Download, FileText, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import MathTextarea from '../components/MathTextarea';

const CreateExam = () => {
  const navigate = useNavigate();
  const { examId } = useParams(); // For edit mode
  const isEditMode = Boolean(examId);
  
  const [examData, setExamData] = useState({
    title: '',
    duration_minutes: 60,
    board_id: '',
    grade_id: '',
    subject_id: '',
    assessment_type_id: '',
    start_datetime: '',
    end_datetime: '',
    questions: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingExam, setLoadingExam] = useState(isEditMode);
  const [enableScheduling, setEnableScheduling] = useState(false);
  
  // Board, Grade, Subject, Assessment Type options
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Question Bank Import (Premium Feature)
  const [hasQuestionBankAccess, setHasQuestionBankAccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFilters, setImportFilters] = useState({
    board_id: '',
    subject_id: '',
    topic_id: '',
    question_type: '',
    difficulty_level: '',
    search_text: ''
  });
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [qbTopics, setQbTopics] = useState([]);
  
  // Marking Scheme State
  const [showMarkingScheme, setShowMarkingScheme] = useState({});

  useEffect(() => {
    fetchOptions();
    checkQuestionBankAccess();
    if (isEditMode) {
      fetchExam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const checkQuestionBankAccess = async () => {
    try {
      const response = await api.get('/question-bank/teacher/access-check');
      setHasQuestionBankAccess(response.data.has_access);
    } catch (err) {
      setHasQuestionBankAccess(false);
    }
  };

  const fetchExam = async () => {
    try {
      const response = await api.get(`/exams/${examId}`);
      const exam = response.data;
      
      // Check if exam is editable (draft status only)
      if (exam.status !== 'draft') {
        toast.error('Only draft exams can be edited');
        navigate(-1);
        return;
      }
      
      setExamData({
        title: exam.title || '',
        duration_minutes: exam.duration_minutes || 60,
        board_id: exam.board_id || '',
        grade_id: exam.grade_id || '',
        subject_id: exam.subject_id || '',
        assessment_type_id: exam.assessment_type_id || '',
        start_datetime: exam.start_datetime || '',
        end_datetime: exam.end_datetime || '',
        questions: exam.questions || [],
      });
      
      // Enable scheduling if dates are set
      if (exam.start_datetime || exam.end_datetime) {
        setEnableScheduling(true);
      }
    } catch (err) {
      toast.error('Failed to load exam');
      navigate(-1);
    } finally {
      setLoadingExam(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const [boardsRes, gradesRes, subjectsRes, assessmentTypesRes] = await Promise.all([
        api.get('/boards'),
        api.get('/grades'),
        api.get('/subjects'),
        api.get('/igcse/assessment-types'),
      ]);
      setBoards(boardsRes.data);
      setGrades(gradesRes.data);
      setSubjects(subjectsRes.data);
      setAssessmentTypes(assessmentTypesRes.data || []);
    } catch (err) {
      toast.error('Failed to load options');
    } finally {
      setLoadingOptions(false);
    }
  };

  // Question Bank Import Functions
  const fetchQBTopics = async (boardId, subjectId) => {
    if (!boardId || !subjectId) {
      setQbTopics([]);
      return;
    }
    try {
      const response = await api.get(`/question-bank/topics?board_id=${boardId}&subject_id=${subjectId}`);
      setQbTopics(response.data);
    } catch (err) {
      setQbTopics([]);
    }
  };

  const searchQuestionBank = async () => {
    if (!hasQuestionBankAccess) return;
    
    setLoadingQuestions(true);
    try {
      const params = new URLSearchParams();
      if (importFilters.board_id) params.append('board_id', importFilters.board_id);
      if (importFilters.subject_id) params.append('subject_id', importFilters.subject_id);
      if (importFilters.topic_id) params.append('topic_id', importFilters.topic_id);
      if (importFilters.question_type) params.append('question_type', importFilters.question_type);
      if (importFilters.difficulty_level) params.append('difficulty_level', importFilters.difficulty_level);
      if (importFilters.search_text) params.append('search_text', importFilters.search_text);
      params.append('limit', '50');
      
      const response = await api.get(`/question-bank/teacher/published-questions?${params.toString()}`);
      setAvailableQuestions(response.data.questions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch questions');
      setAvailableQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const toggleQuestionSelection = (question) => {
    const isSelected = selectedQuestions.some(q => q.id === question.id);
    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const importSelectedQuestions = () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question to import');
      return;
    }

    // Convert Question Bank questions to exam question format
    const newQuestions = selectedQuestions.map(q => {
      if (q.question_type === 'mcq') {
        return {
          question_text: q.question_text || '',
          question_type: 'mcq',
          question_diagram: q.question_diagram || null,
          mcq_options: q.mcq_options?.map(opt => ({
            option_text: opt.text || '',
            option_image: opt.option_image || null,
          })) || [
            { option_text: '', option_image: null },
            { option_text: '', option_image: null },
            { option_text: '', option_image: null },
            { option_text: '', option_image: null },
          ],
          correct_option: q.mcq_options?.findIndex(opt => opt.is_correct) ?? null,
          marks: q.total_marks || 1,
          negative_marks: q.negative_marks || 0,
          parts: [],
          imported_from_qb: q.id,
        };
      } else {
        // Structured/Descriptive question
        return {
          question_text: q.question_text || '',
          question_type: 'descriptive',
          parts: q.parts?.map(p => ({
            part_label: p.part_label || '',
            part_text: p.part_text || '',
            diagram_base64: p.diagram_base64 || null,
            has_drawing: p.has_drawing || false,
            marking_scheme: p.marking_scheme || '',
            model_answer: p.model_answer || '',
            sub_parts: p.sub_parts?.map(sp => ({
              sub_part_label: sp.sub_part_label || '',
              sub_part_text: sp.sub_part_text || '',
              marks: sp.marks || 1,
              assessment_objective: sp.assessment_objective || '',
              has_drawing: sp.has_drawing || false,
              diagram_base64: sp.diagram_base64 || null,
              marking_scheme: sp.marking_scheme || '',
              model_answer: sp.model_answer || '',
            })) || null,
            marks: p.marks || 1,
            assessment_objective: p.assessment_objective || '',
          })) || [{
            part_label: '',
            part_text: '',
            diagram_base64: null,
            has_drawing: false,
            sub_parts: null,
            marks: 1,
            assessment_objective: '',
            marking_scheme: '',
            model_answer: '',
          }],
          imported_from_qb: q.id,
        };
      }
    });

    setExamData(prev => ({
      ...prev,
      questions: [...prev.questions, ...newQuestions]
    }));

    toast.success(`Imported ${selectedQuestions.length} question(s) from Question Bank`);
    setShowImportModal(false);
    setSelectedQuestions([]);
    setAvailableQuestions([]);
  };

  const openImportModal = () => {
    // Pre-fill filters with exam's board and subject if available
    setImportFilters({
      board_id: examData.board_id || '',
      subject_id: examData.subject_id || '',
      topic_id: '',
      question_type: '',
      difficulty_level: '',
      search_text: ''
    });
    if (examData.board_id && examData.subject_id) {
      fetchQBTopics(examData.board_id, examData.subject_id);
    }
    setSelectedQuestions([]);
    setAvailableQuestions([]);
    setShowImportModal(true);
  };

  const addQuestion = (type = 'descriptive') => {
    if (type === 'mcq') {
      setExamData({
        ...examData,
        questions: [
          ...examData.questions,
          {
            question_text: '',
            question_type: 'mcq',
            question_diagram: null,
            mcq_options: [
              { option_text: '', option_image: null },
              { option_text: '', option_image: null },
              { option_text: '', option_image: null },
              { option_text: '', option_image: null },
            ],
            correct_option: null,
            marks: 1,
            negative_marks: 0,
            assessment_objective: 'AO1',  // Default AO for MCQ
            parts: [],
          },
        ],
      });
    } else {
      setExamData({
        ...examData,
        questions: [
          ...examData.questions,
          {
            question_text: '',
            question_type: 'descriptive',
            parts: [{ 
              part_label: '',
              part_text: '', 
              diagram_base64: null, 
              has_drawing: false,
              sub_parts: null,
              marks: null,
              assessment_objective: null,
            }],
            marks: 10,
            negative_marks: 0,
            question_diagram: null,
            mcq_options: null,
            correct_option: null,
          },
        ],
      });
    }
  };

  const removeQuestion = (qIndex) => {
    const newQuestions = examData.questions.filter((_, i) => i !== qIndex);
    setExamData({ ...examData, questions: newQuestions });
  };

  const updateQuestion = (qIndex, field, value) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex][field] = value;
    setExamData({ ...examData, questions: newQuestions });
  };

  const addPart = (qIndex) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex].parts.push({
      part_label: '',
      part_text: '',
      diagram_base64: null,
      has_drawing: false,
      sub_parts: null,  // No sub-parts by default (simple mode)
      marks: null,
      assessment_objective: null,
    });
    setExamData({ ...examData, questions: newQuestions });
  };

  const removePart = (qIndex, pIndex) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex].parts = newQuestions[qIndex].parts.filter((_, i) => i !== pIndex);
    setExamData({ ...examData, questions: newQuestions });
  };

  const updatePart = (qIndex, pIndex, field, value) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex].parts[pIndex][field] = value;
    setExamData({ ...examData, questions: newQuestions });
  };

  // IGCSE Sub-part functions
  const addSubPart = (qIndex, pIndex) => {
    const newQuestions = [...examData.questions];
    const part = newQuestions[qIndex].parts[pIndex];
    if (!part.sub_parts) {
      part.sub_parts = [];
    }
    part.sub_parts.push({
      sub_part_label: '',
      sub_part_text: '',
      marks: 1,
      assessment_objective: null,
      has_drawing: false,
      diagram_base64: null,
    });
    setExamData({ ...examData, questions: newQuestions });
  };

  const removeSubPart = (qIndex, pIndex, spIndex) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex].parts[pIndex].sub_parts = 
      newQuestions[qIndex].parts[pIndex].sub_parts.filter((_, i) => i !== spIndex);
    // If no sub-parts left, set to null
    if (newQuestions[qIndex].parts[pIndex].sub_parts.length === 0) {
      newQuestions[qIndex].parts[pIndex].sub_parts = null;
    }
    setExamData({ ...examData, questions: newQuestions });
  };

  const updateSubPart = (qIndex, pIndex, spIndex, field, value) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex].parts[pIndex].sub_parts[spIndex][field] = value;
    setExamData({ ...examData, questions: newQuestions });
  };

  const handleSubPartImageUpload = (qIndex, pIndex, spIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateSubPart(qIndex, pIndex, spIndex, 'diagram_base64', reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Toggle marking scheme visibility for a question
  const toggleMarkingScheme = (qIndex) => {
    setShowMarkingScheme(prev => ({
      ...prev,
      [qIndex]: !prev[qIndex]
    }));
  };

  // Check if a question has any marking scheme data
  const hasMarkingSchemeData = (question) => {
    if (!question.parts) return false;
    return question.parts.some(part => {
      if (part.marking_scheme || part.model_answer) return true;
      if (part.sub_parts) {
        return part.sub_parts.some(sp => sp.marking_scheme || sp.model_answer);
      }
      return false;
    });
  };

  // Calculate total marks for a question based on parts/sub-parts
  const calculateQuestionMarks = (question) => {
    let total = 0;
    question.parts.forEach(part => {
      if (part.sub_parts && part.sub_parts.length > 0) {
        total += part.sub_parts.reduce((sum, sp) => sum + (sp.marks || 0), 0);
      } else if (part.marks) {
        total += part.marks;
      }
    });
    return total || question.marks;
  };

  const handleImageUpload = (qIndex, pIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updatePart(qIndex, pIndex, 'diagram_base64', reader.result);
    };
    reader.readAsDataURL(file);
  };

  // MCQ specific functions
  const updateMCQOption = (qIndex, optIndex, field, value) => {
    const newQuestions = [...examData.questions];
    newQuestions[qIndex].mcq_options[optIndex][field] = value;
    setExamData({ ...examData, questions: newQuestions });
  };

  const handleMCQOptionImageUpload = (qIndex, optIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateMCQOption(qIndex, optIndex, 'option_image', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleQuestionDiagramUpload = (qIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateQuestion(qIndex, 'question_diagram', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const setCorrectOption = (qIndex, optIndex) => {
    updateQuestion(qIndex, 'correct_option', optIndex);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (examData.questions.length === 0) {
      toast.error('Please add at least one question', {
        duration: 3000,
      });
      return;
    }
    
    // Validate MCQ questions have correct answers
    for (let i = 0; i < examData.questions.length; i++) {
      const q = examData.questions[i];
      if (q.question_type === 'mcq' && q.correct_option === null) {
        toast.error(`Please select the correct answer for MCQ Question ${i + 1}`, {
          duration: 3000,
        });
        return;
      }
    }
    
    // Check if board, grade, subject are selected
    if (!examData.board_id || !examData.grade_id || !examData.subject_id) {
      toast.error('Please select Board, Grade, and Subject', {
        duration: 3000,
      });
      return;
    }
    
    // Check if assessment type is selected (REQUIRED)
    if (!examData.assessment_type_id) {
      toast.error('Please select an Assessment Type (CT, UT, TE, or ME)', {
        duration: 3000,
      });
      return;
    }

    // Validate scheduling dates if enabled
    if (enableScheduling) {
      if (!examData.start_datetime || !examData.end_datetime) {
        toast.error('Please set both start and end date/time for scheduled exam', {
          duration: 3000,
        });
        return;
      }
      if (new Date(examData.start_datetime) >= new Date(examData.end_datetime)) {
        toast.error('End date/time must be after start date/time', {
          duration: 3000,
        });
        return;
      }
    }

    setLoading(true);
    const loadingToast = toast.loading(isEditMode ? 'Saving changes...' : 'Creating exam...');
    
    try {
      // Prepare data - convert local datetime to ISO format for backend
      const submitData = {
        ...examData,
        start_datetime: enableScheduling && examData.start_datetime 
          ? new Date(examData.start_datetime).toISOString() 
          : null,
        end_datetime: enableScheduling && examData.end_datetime 
          ? new Date(examData.end_datetime).toISOString() 
          : null,
      };
      
      if (isEditMode) {
        // Update existing exam
        await api.put(`/exams/${examId}`, submitData);
        toast.dismiss(loadingToast);
        toast.success('Exam updated successfully!', {
          duration: 2000,
        });
      } else {
        // Create new exam
        await api.post('/exams', submitData);
        toast.dismiss(loadingToast);
        toast.success('Exam created successfully!', {
          duration: 2000,
        });
      }
      
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} exam. Please try again.`, {
        duration: 4000,
      });
      setLoading(false);
    }
  };

  if (loadingExam) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading exam...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-700 transition-colors mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-slate-900">
            {isEditMode ? 'Edit Exam' : 'Create New Exam'}
          </h1>
          {isEditMode && (
            <p className="text-sm text-amber-600 mt-1">
              Editing draft exam - changes will be saved when you click "{isEditMode ? 'Save Changes' : 'Create Exam'}"
            </p>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Exam Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Exam Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Exam Title
                </label>
                <input
                  type="text"
                  data-testid="exam-title-input"
                  required
                  value={examData.title}
                  onChange={(e) => setExamData({ ...examData, title: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  placeholder="e.g., Mathematics Final Exam"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Duration (minutes)
                </label>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <input
                    type="number"
                    data-testid="exam-duration-input"
                    required
                    min="1"
                    value={examData.duration_minutes}
                    onChange={(e) => setExamData({ ...examData, duration_minutes: parseInt(e.target.value) })}
                    className="w-32 h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  />
                </div>
              </div>

              {/* Exam Scheduling Section */}
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setEnableScheduling(!enableScheduling)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      enableScheduling ? 'bg-indigo-600' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        enableScheduling ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    Schedule Exam (Set Start & End Time)
                  </label>
                </div>

                {enableScheduling && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Start Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        data-testid="exam-start-datetime"
                        value={examData.start_datetime}
                        onChange={(e) => setExamData({ ...examData, start_datetime: e.target.value })}
                        className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">Students can start taking the exam from this time</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        End Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        data-testid="exam-end-datetime"
                        value={examData.end_datetime}
                        onChange={(e) => setExamData({ ...examData, end_datetime: e.target.value })}
                        min={examData.start_datetime}
                        className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">Exam will no longer be available after this time</p>
                    </div>
                    {examData.start_datetime && examData.end_datetime && (
                      <div className="md:col-span-2 p-3 bg-white rounded-lg border border-indigo-200">
                        <p className="text-sm text-indigo-700">
                          <strong>Exam Window:</strong> {new Date(examData.start_datetime).toLocaleString()} — {new Date(examData.end_datetime).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Board, Grade, Subject Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-indigo-600" />
                      Board
                    </div>
                  </label>
                  {loadingOptions ? (
                    <div className="h-11 bg-slate-100 rounded-lg animate-pulse" />
                  ) : boards.length > 0 ? (
                    <select
                      value={examData.board_id}
                      onChange={(e) => setExamData({ ...examData, board_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="">Select Board</option>
                      {boards.map(board => (
                        <option key={board.id} value={board.id}>{board.name}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/teacher/settings')}
                      className="w-full h-11 px-4 rounded-lg border border-dashed border-slate-300 text-slate-500 text-sm hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                    >
                      + Add Boards
                    </button>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-green-600" />
                      Grade
                    </div>
                  </label>
                  {loadingOptions ? (
                    <div className="h-11 bg-slate-100 rounded-lg animate-pulse" />
                  ) : grades.length > 0 ? (
                    <select
                      value={examData.grade_id}
                      onChange={(e) => setExamData({ ...examData, grade_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    >
                      <option value="">Select Grade</option>
                      {grades.map(grade => (
                        <option key={grade.id} value={grade.id}>{grade.name}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/teacher/settings')}
                      className="w-full h-11 px-4 rounded-lg border border-dashed border-slate-300 text-slate-500 text-sm hover:border-green-400 hover:text-green-600 transition-colors"
                    >
                      + Add Grades
                    </button>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-amber-600" />
                      Subject
                    </div>
                  </label>
                  {loadingOptions ? (
                    <div className="h-11 bg-slate-100 rounded-lg animate-pulse" />
                  ) : subjects.length > 0 ? (
                    <select
                      value={examData.subject_id}
                      onChange={(e) => setExamData({ ...examData, subject_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      required
                    >
                      <option value="">Select Subject</option>
                      {subjects.map(subject => (
                        <option key={subject.id} value={subject.id}>{subject.name}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/teacher/settings')}
                      className="w-full h-11 px-4 rounded-lg border border-dashed border-slate-300 text-slate-500 text-sm hover:border-amber-400 hover:text-amber-600 transition-colors"
                    >
                      + Add Subjects
                    </button>
                  )}
                </div>
              </div>
              
              {/* Assessment Type - REQUIRED */}
              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                  <Layers className="w-4 h-4 text-purple-600" />
                  Assessment Type <span className="text-red-500">*</span>
                </label>
                {loadingOptions ? (
                  <div className="h-11 bg-slate-100 rounded-lg animate-pulse" />
                ) : assessmentTypes.length > 0 ? (
                  <select
                    value={examData.assessment_type_id}
                    onChange={(e) => setExamData({ ...examData, assessment_type_id: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                    data-testid="assessment-type-select"
                  >
                    <option value="">Select Assessment Type</option>
                    {assessmentTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.code} - {type.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                    No assessment types found. Please contact school admin to set up assessment types (CT, UT, TE, ME).
                  </div>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  CT = Class Test, UT = Unit Test, TE = Term Exam, ME = Mock Exam
                </p>
              </div>
              
              {(boards.length === 0 || grades.length === 0 || subjects.length === 0) && !loadingOptions && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Please add boards, grades, and subjects in <button type="button" onClick={() => navigate('/teacher/settings')} className="font-medium underline">Settings</button> before creating an exam.
                </div>
              )}
            </div>
          </div>

          {/* Questions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">Questions</h2>
              <div className="flex items-center gap-2">
                {hasQuestionBankAccess && (
                  <button
                    type="button"
                    data-testid="import-from-qb-btn"
                    onClick={openImportModal}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all"
                  >
                    <Download className="w-5 h-5" />
                    Import from Question Bank
                  </button>
                )}
                <button
                  type="button"
                  data-testid="add-descriptive-btn"
                  onClick={() => addQuestion('descriptive')}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                >
                  <Plus className="w-5 h-5" />
                  Add Descriptive
                </button>
                <button
                  type="button"
                  data-testid="add-mcq-btn"
                  onClick={() => addQuestion('mcq')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all"
                >
                  <Plus className="w-5 h-5" />
                  Add MCQ
                </button>
              </div>
            </div>

            {examData.questions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <p className="text-slate-600 mb-4">No questions added yet</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  {hasQuestionBankAccess && (
                    <button
                      type="button"
                      onClick={openImportModal}
                      className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all"
                    >
                      Import from Question Bank
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => addQuestion('descriptive')}
                    className="px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                  >
                    Add Descriptive Question
                  </button>
                  <button
                    type="button"
                    onClick={() => addQuestion('mcq')}
                    className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all"
                  >
                    Add MCQ Question
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {examData.questions.map((question, qIndex) => (
                  <div
                    key={qIndex}
                    data-testid={`question-${qIndex}`}
                    className={`bg-white rounded-xl border-2 p-6 ${
                      question.question_type === 'mcq' ? 'border-green-200' : 'border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-slate-900">
                          Question {qIndex + 1}
                        </h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          question.question_type === 'mcq' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-indigo-100 text-indigo-700'
                        }`}>
                          {question.question_type === 'mcq' ? 'MCQ' : 'Descriptive'}
                        </span>
                        {hasMarkingSchemeData(question) && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                            Has Marking Scheme
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {question.question_type === 'descriptive' && (
                          <button
                            type="button"
                            data-testid={`toggle-marking-scheme-${qIndex}`}
                            onClick={() => toggleMarkingScheme(qIndex)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              showMarkingScheme[qIndex]
                                ? 'bg-amber-100 text-amber-700 border border-amber-200'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            <ClipboardList className="w-4 h-4" />
                            {showMarkingScheme[qIndex] ? 'Hide Marking Scheme' : 'Add Marking Scheme'}
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`remove-question-${qIndex}`}
                          onClick={() => removeQuestion(qIndex)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Question Text
                        </label>
                        <MathTextarea
                          data-testid={`question-text-${qIndex}`}
                          required
                          value={question.question_text}
                          onChange={(val) => updateQuestion(qIndex, 'question_text', val)}
                          rows={3}
                          placeholder="Enter question text... (Use toolbar for math symbols)"
                        />
                      </div>

                      {/* Question Diagram (for MCQ) */}
                      {question.question_type === 'mcq' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Question Diagram (Optional)
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleQuestionDiagramUpload(qIndex, e)}
                            className="hidden"
                            id={`question-diagram-${qIndex}`}
                          />
                          <label
                            htmlFor={`question-diagram-${qIndex}`}
                            className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 transition-colors"
                          >
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-slate-500">Upload diagram for question</span>
                          </label>
                          {question.question_diagram && (
                            <div className="mt-3 relative">
                              <img
                                src={question.question_diagram}
                                alt="Question diagram"
                                className="max-h-48 rounded-lg border border-slate-200"
                              />
                              <button
                                type="button"
                                onClick={() => updateQuestion(qIndex, 'question_diagram', null)}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Marks
                          </label>
                          <input
                            type="number"
                            data-testid={`question-marks-${qIndex}`}
                            required
                            min="1"
                            value={question.marks}
                            onChange={(e) => updateQuestion(qIndex, 'marks', parseInt(e.target.value) || 0)}
                            className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        {question.question_type === 'mcq' && (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Negative Marks (for wrong answer)
                            </label>
                            <input
                              type="number"
                              step="0.25"
                              min="0"
                              value={question.negative_marks || 0}
                              onChange={(e) => updateQuestion(qIndex, 'negative_marks', parseFloat(e.target.value) || 0)}
                              className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        )}
                        {question.question_type === 'mcq' && (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Assessment Objective
                            </label>
                            <select
                              value={question.assessment_objective || 'AO1'}
                              onChange={(e) => updateQuestion(qIndex, 'assessment_objective', e.target.value)}
                              className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="AO1">AO1 - Knowledge & Understanding</option>
                              <option value="AO2">AO2 - Application</option>
                              <option value="AO3">AO3 - Analysis & Evaluation</option>
                            </select>
                          </div>
                        )}
                      </div>

                      {/* MCQ Options */}
                      {question.question_type === 'mcq' && question.mcq_options && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-3">
                            Options (Click to mark correct answer)
                          </label>
                          <div className="space-y-3">
                            {question.mcq_options.map((option, optIndex) => (
                              <div key={optIndex} className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                                question.correct_option === optIndex 
                                  ? 'border-green-500 bg-green-50' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}>
                                <button
                                  type="button"
                                  onClick={() => setCorrectOption(qIndex, optIndex)}
                                  className="mt-1 flex-shrink-0"
                                >
                                  {question.correct_option === optIndex ? (
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                  ) : (
                                    <Circle className="w-6 h-6 text-slate-300" />
                                  )}
                                </button>
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-700">
                                      {String.fromCharCode(65 + optIndex)}.
                                    </span>
                                    <input
                                      type="text"
                                      value={option.option_text || ''}
                                      onChange={(e) => updateMCQOption(qIndex, optIndex, 'option_text', e.target.value)}
                                      placeholder={`Option ${String.fromCharCode(65 + optIndex)} text...`}
                                      className="flex-1 h-10 px-3 rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                  </div>
                                  {/* Option Image Upload */}
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleMCQOptionImageUpload(qIndex, optIndex, e)}
                                      className="hidden"
                                      id={`option-image-${qIndex}-${optIndex}`}
                                    />
                                    <label
                                      htmlFor={`option-image-${qIndex}-${optIndex}`}
                                      className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-1"
                                    >
                                      <Upload className="w-3 h-3" />
                                      {option.option_image ? 'Change image' : 'Add image'}
                                    </label>
                                    {option.option_image && (
                                      <>
                                        <img
                                          src={option.option_image}
                                          alt={`Option ${String.fromCharCode(65 + optIndex)}`}
                                          className="h-16 rounded border border-slate-200"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => updateMCQOption(qIndex, optIndex, 'option_image', null)}
                                          className="text-red-500 hover:text-red-700"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {question.correct_option === null && (
                            <p className="mt-2 text-sm text-amber-600">
                              Please click on an option to mark it as the correct answer
                            </p>
                          )}
                        </div>
                      )}

                      {/* Parts - Only for Descriptive Questions */}
                      {question.question_type === 'descriptive' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-slate-700">
                            Parts (IGCSE Structure Supported)
                          </label>
                          <button
                            type="button"
                            data-testid={`add-part-${qIndex}`}
                            onClick={() => addPart(qIndex)}
                            className="text-sm text-indigo-700 hover:text-indigo-800 font-medium"
                          >
                            + Add Part
                          </button>
                        </div>

                        <div className="space-y-4">
                          {question.parts.map((part, pIndex) => (
                            <div
                              key={pIndex}
                              data-testid={`part-${qIndex}-${pIndex}`}
                              className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-slate-700">
                                  Part {String.fromCharCode(97 + pIndex)})
                                </span>
                                <div className="flex items-center gap-2">
                                  {!part.sub_parts && (
                                    <button
                                      type="button"
                                      onClick={() => addSubPart(qIndex, pIndex)}
                                      className="text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1 bg-amber-50 rounded"
                                    >
                                      + Add Sub-parts (IGCSE)
                                    </button>
                                  )}
                                  {question.parts.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removePart(qIndex, pIndex)}
                                      className="text-sm text-red-600 hover:text-red-700"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">
                                    Part Text
                                  </label>
                                  <MathTextarea
                                    data-testid={`part-text-${qIndex}-${pIndex}`}
                                    required
                                    value={part.part_text}
                                    onChange={(val) => updatePart(qIndex, pIndex, 'part_text', val)}
                                    rows={2}
                                    placeholder="Part text (e.g., Calculate the velocity when θ = 30°...)"
                                  />
                                </div>

                                {/* Simple Part Controls (no sub-parts) */}
                                {!part.sub_parts && (
                                  <>
                                    <div className="flex items-center gap-4 flex-wrap">
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          data-testid={`part-drawing-${qIndex}-${pIndex}`}
                                          checked={part.has_drawing}
                                          onChange={(e) => updatePart(qIndex, pIndex, 'has_drawing', e.target.checked)}
                                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-slate-600">Requires drawing</span>
                                      </label>

                                      <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-all">
                                        <Upload className="w-4 h-4 text-slate-600" />
                                        <span className="text-sm text-slate-600">
                                          {part.diagram_base64 ? 'Change Diagram' : 'Upload Diagram'}
                                        </span>
                                        <input
                                          type="file"
                                          data-testid={`part-diagram-${qIndex}-${pIndex}`}
                                          accept="image/*"
                                          onChange={(e) => handleImageUpload(qIndex, pIndex, e)}
                                          className="hidden"
                                        />
                                      </label>

                                      <div className="flex items-center gap-2">
                                        <label className="text-sm text-slate-600">Marks:</label>
                                        <input
                                          type="number"
                                          min="1"
                                          value={part.marks || ''}
                                          onChange={(e) => updatePart(qIndex, pIndex, 'marks', parseInt(e.target.value) || null)}
                                          className="w-16 h-8 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                          placeholder="0"
                                        />
                                      </div>

                                      <div className="flex items-center gap-2">
                                        <label className="text-sm text-slate-600">AO:</label>
                                        <select
                                          value={part.assessment_objective || ''}
                                          onChange={(e) => updatePart(qIndex, pIndex, 'assessment_objective', e.target.value || null)}
                                          className="h-8 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                          <option value="">None</option>
                                          <option value="AO1">AO1</option>
                                          <option value="AO2">AO2</option>
                                          <option value="AO3">AO3</option>
                                        </select>
                                      </div>
                                    </div>

                                    {part.diagram_base64 && (
                                      <div className="relative inline-block">
                                        <img
                                          src={part.diagram_base64}
                                          alt="Diagram preview"
                                          className="max-w-xs rounded-lg border border-slate-200"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => updatePart(qIndex, pIndex, 'diagram_base64', null)}
                                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    )}

                                    {/* Marking Scheme for Part (when no sub-parts) */}
                                    {showMarkingScheme[qIndex] && !part.sub_parts && (
                                      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                        <label className="block text-sm font-medium text-amber-800 mb-2">
                                          Marking Scheme for Part {String.fromCharCode(97 + pIndex)}
                                        </label>
                                        <textarea
                                          data-testid={`marking-scheme-${qIndex}-${pIndex}`}
                                          value={part.marking_scheme || ''}
                                          onChange={(e) => updatePart(qIndex, pIndex, 'marking_scheme', e.target.value)}
                                          rows={3}
                                          className="w-full px-3 py-2 text-sm rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                          placeholder="Enter marking points (e.g., 1 mark for correct formula, 1 mark for substitution, 1 mark for final answer)"
                                        />
                                        <label className="block text-sm font-medium text-amber-800 mt-2 mb-1">
                                          Model Answer (Optional)
                                        </label>
                                        <textarea
                                          data-testid={`model-answer-${qIndex}-${pIndex}`}
                                          value={part.model_answer || ''}
                                          onChange={(e) => updatePart(qIndex, pIndex, 'model_answer', e.target.value)}
                                          rows={2}
                                          className="w-full px-3 py-2 text-sm rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                          placeholder="Enter model answer"
                                        />
                                      </div>
                                    )}
                                  </>
                                )}

                                {/* IGCSE Sub-parts */}
                                {part.sub_parts && (
                                  <div className="mt-4 pl-4 border-l-2 border-amber-300">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                                        Sub-parts (IGCSE)
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => addSubPart(qIndex, pIndex)}
                                        className="text-xs text-amber-600 hover:text-amber-700"
                                      >
                                        + Add Sub-part
                                      </button>
                                    </div>
                                    
                                    <div className="space-y-3">
                                      {part.sub_parts.map((subPart, spIndex) => (
                                        <div key={spIndex} className="p-3 bg-white rounded-lg border border-amber-200">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-amber-800">
                                              {String.fromCharCode(97 + pIndex)}({['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'][spIndex] || spIndex + 1})
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => removeSubPart(qIndex, pIndex, spIndex)}
                                              className="text-xs text-red-500 hover:text-red-700"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                          
                                          <div className="mb-2">
                                            <MathTextarea
                                              required
                                              value={subPart.sub_part_text}
                                              onChange={(val) => updateSubPart(qIndex, pIndex, spIndex, 'sub_part_text', val)}
                                              rows={2}
                                              placeholder="Sub-part question (e.g., Find the value of x² when...)"
                                            />
                                          </div>
                                          
                                          <div className="flex items-center gap-3 flex-wrap">
                                            <div className="flex items-center gap-1">
                                              <label className="text-xs text-slate-600">Marks:</label>
                                              <input
                                                type="number"
                                                min="1"
                                                value={subPart.marks}
                                                onChange={(e) => updateSubPart(qIndex, pIndex, spIndex, 'marks', parseInt(e.target.value) || 1)}
                                                className="w-14 h-7 px-2 text-xs rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                              />
                                            </div>
                                            
                                            <div className="flex items-center gap-1">
                                              <label className="text-xs text-slate-600">AO:</label>
                                              <select
                                                value={subPart.assessment_objective || ''}
                                                onChange={(e) => updateSubPart(qIndex, pIndex, spIndex, 'assessment_objective', e.target.value || null)}
                                                className="h-7 px-1 text-xs rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                              >
                                                <option value="">-</option>
                                                <option value="AO1">AO1</option>
                                                <option value="AO2">AO2</option>
                                                <option value="AO3">AO3</option>
                                              </select>
                                            </div>
                                            
                                            <label className="flex items-center gap-1 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={subPart.has_drawing}
                                                onChange={(e) => updateSubPart(qIndex, pIndex, spIndex, 'has_drawing', e.target.checked)}
                                                className="w-3 h-3 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                              />
                                              <span className="text-xs text-slate-600">Drawing</span>
                                            </label>
                                            
                                            <label className="flex items-center gap-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded cursor-pointer hover:bg-slate-100 text-xs">
                                              <Upload className="w-3 h-3 text-slate-500" />
                                              <span className="text-slate-600">
                                                {subPart.diagram_base64 ? 'Change' : 'Diagram'}
                                              </span>
                                              <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleSubPartImageUpload(qIndex, pIndex, spIndex, e)}
                                                className="hidden"
                                              />
                                            </label>
                                          </div>
                                          
                                          {subPart.diagram_base64 && (
                                            <div className="relative inline-block mt-2">
                                              <img
                                                src={subPart.diagram_base64}
                                                alt="Sub-part diagram"
                                                className="max-w-[150px] rounded border border-slate-200"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => updateSubPart(qIndex, pIndex, spIndex, 'diagram_base64', null)}
                                                className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded-full"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                          )}

                                          {/* Marking Scheme for Sub-part */}
                                          {showMarkingScheme[qIndex] && (
                                            <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                                              <label className="block text-xs font-medium text-amber-800 mb-1">
                                                Marking Scheme
                                              </label>
                                              <textarea
                                                data-testid={`subpart-marking-scheme-${qIndex}-${pIndex}-${spIndex}`}
                                                value={subPart.marking_scheme || ''}
                                                onChange={(e) => updateSubPart(qIndex, pIndex, spIndex, 'marking_scheme', e.target.value)}
                                                rows={2}
                                                className="w-full px-2 py-1.5 text-xs rounded border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                                placeholder="e.g., Award 1 mark for correct method, 1 mark for answer"
                                              />
                                              <label className="block text-xs font-medium text-amber-800 mt-1.5 mb-1">
                                                Model Answer
                                              </label>
                                              <textarea
                                                data-testid={`subpart-model-answer-${qIndex}-${pIndex}-${spIndex}`}
                                                value={subPart.model_answer || ''}
                                                onChange={(e) => updateSubPart(qIndex, pIndex, spIndex, 'model_answer', e.target.value)}
                                                rows={1}
                                                className="w-full px-2 py-1.5 text-xs rounded border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                                placeholder="Expected answer"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/teacher/dashboard')}
              className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="submit-exam-btn"
              disabled={loading || examData.questions.length === 0}
              className="px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                isEditMode ? 'Saving...' : 'Creating...'
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Save Changes' : 'Create Exam'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Import from Question Bank Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Download className="w-5 h-5 text-purple-600" />
                Import from Question Bank
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Filters */}
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Board</label>
                  <select
                    value={importFilters.board_id}
                    onChange={(e) => {
                      setImportFilters(prev => ({ ...prev, board_id: e.target.value, topic_id: '' }));
                      if (e.target.value && importFilters.subject_id) {
                        fetchQBTopics(e.target.value, importFilters.subject_id);
                      }
                    }}
                    className="w-full h-9 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Boards</option>
                    {boards.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
                  <select
                    value={importFilters.subject_id}
                    onChange={(e) => {
                      setImportFilters(prev => ({ ...prev, subject_id: e.target.value, topic_id: '' }));
                      if (importFilters.board_id && e.target.value) {
                        fetchQBTopics(importFilters.board_id, e.target.value);
                      }
                    }}
                    className="w-full h-9 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Subjects</option>
                    {subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Topic</label>
                  <select
                    value={importFilters.topic_id}
                    onChange={(e) => setImportFilters(prev => ({ ...prev, topic_id: e.target.value }))}
                    className="w-full h-9 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={qbTopics.length === 0}
                  >
                    <option value="">All Topics</option>
                    {qbTopics.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                  <select
                    value={importFilters.question_type}
                    onChange={(e) => setImportFilters(prev => ({ ...prev, question_type: e.target.value }))}
                    className="w-full h-9 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Types</option>
                    <option value="mcq">MCQ</option>
                    <option value="structured">Structured</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Difficulty</label>
                  <select
                    value={importFilters.difficulty_level}
                    onChange={(e) => setImportFilters(prev => ({ ...prev, difficulty_level: e.target.value }))}
                    className="w-full h-9 px-2 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Levels</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={importFilters.search_text}
                    onChange={(e) => setImportFilters(prev => ({ ...prev, search_text: e.target.value }))}
                    className="w-full h-9 pl-9 pr-3 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={searchQuestionBank}
                  className="px-4 h-9 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded transition-all"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Questions List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingQuestions ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-slate-600">Searching questions...</p>
                </div>
              ) : availableQuestions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No questions found. Try adjusting your filters and click Search.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableQuestions.map(q => {
                    const isSelected = selectedQuestions.some(sq => sq.id === q.id);
                    return (
                      <div
                        key={q.id}
                        onClick={() => toggleQuestionSelection(q)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-slate-200 hover:border-purple-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isSelected ? 'bg-purple-600' : 'bg-slate-200'
                          }`}>
                            {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                                {q.question_code || 'No Code'}
                              </span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                q.question_type === 'mcq' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {q.question_type === 'mcq' ? 'MCQ' : 'Structured'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                q.difficulty_level === 'easy' ? 'bg-green-50 text-green-600' :
                                q.difficulty_level === 'hard' ? 'bg-red-50 text-red-600' :
                                'bg-yellow-50 text-yellow-600'
                              }`}>
                                {q.difficulty_level}
                              </span>
                              <span className="text-xs text-slate-500">{q.total_marks} marks</span>
                            </div>
                            <p className="text-sm text-slate-700 line-clamp-2">
                              {q.question_text || 'No question text'}
                            </p>
                            {q.parts && q.parts.length > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                {q.parts.length} part(s)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {selectedQuestions.length > 0 ? (
                  <span className="text-purple-700 font-medium">{selectedQuestions.length} question(s) selected</span>
                ) : (
                  <span>Click on questions to select them</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={importSelectedQuestions}
                  disabled={selectedQuestions.length === 0}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-all"
                >
                  Import {selectedQuestions.length > 0 && `(${selectedQuestions.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateExam;